import React, { useEffect, useState, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Autocomplete,
    TextField,
    Box,
    LinearProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    Chip,
} from "@mui/material";
import { useInstance } from "react-ioc";
import { ApiClients } from "app/modules/company/services/AxiosBaseClient";
import {
    OrderSourceType,
    OrderVehicleMatchDebugRequest,
    OrderVehicleMatchDebugOrderRequest,
    OrderVehicleMatchDebugResponse,
    VehicleShortListModel,
} from "app/modules/company/api/clients.api";
import { CacheService } from "app/modules/company/services/CacheService";

const LOCAL_STORAGE_KEY = "externalOrdersInspectVehicleId";

export interface InspectOrderVehicleMatchModalProps {
    open: boolean;
    onClose: () => void;
    orderId: number;
    orderDisplay?: string;
}

export const InspectOrderVehicleMatchModal: React.FC<InspectOrderVehicleMatchModalProps> = ({
    open,
    onClose,
    orderId,
    orderDisplay,
}) => {
    const apiClients = useInstance(ApiClients);
    const cacheService = useInstance(CacheService);

    const [vehicles, setVehicles] = useState<VehicleShortListModel[]>([]);
    const [vehiclesLoading, setVehiclesLoading] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<VehicleShortListModel | null>(null);
    const [runLoading, setRunLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [debugResult, setDebugResult] = useState<OrderVehicleMatchDebugResponse | null>(null);

    const vehicleOptions = useMemo(() => vehicles, [vehicles]);
    const filterOptions = (options: VehicleShortListModel[], state) => {
        const input = state.inputValue?.toLowerCase() ?? "";
        if (!input) return options;
        return options.filter(
            (v) =>
                (v.displayName && v.displayName.toLowerCase().includes(input)) ||
                (v.number != null && String(v.number).toLowerCase().includes(input))
        );
    };

    useEffect(() => {
        if (!open) return;
        setError(null);
        setDebugResult(null);
        setVehiclesLoading(true);
        cacheService
            .getVehicles()
            .then((list) => {
                setVehicles(list ?? []);
                const savedId = localStorage.getItem(LOCAL_STORAGE_KEY);
                const id = savedId ? parseInt(savedId, 10) : NaN;
                if (!isNaN(id) && list?.length) {
                    const preselected = list.find((v) => v.id === id);
                    if (preselected) setSelectedVehicle(preselected);
                    else setSelectedVehicle(null);
                } else {
                    setSelectedVehicle(null);
                }
            })
            .finally(() => setVehiclesLoading(false));
    }, [open, orderId, cacheService]);

    const handleRun = async () => {
        if (selectedVehicle?.id == null) return;
        setError(null);
        setRunLoading(true);
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, String(selectedVehicle.id));
            const request = new OrderVehicleMatchDebugRequest({
                vehicleId: selectedVehicle.id,
                orders: [
                    new OrderVehicleMatchDebugOrderRequest({
                        orderId,
                        orderType: OrderSourceType.External,
                    }),
                ],
            });
            const response = await apiClients.radarClient.getOrderVehicleMatchDebug(request);
            setDebugResult(response);
        } catch (e: any) {
            setError(e?.message ?? "Request failed");
            setDebugResult(null);
        } finally {
            setRunLoading(false);
        }
    };

    const orderResult = debugResult?.orderResults?.[0];
    const vehicleSummary = debugResult?.vehicle;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Inspect order–vehicle match
                {orderDisplay != null && (
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                        Order {orderDisplay}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <Autocomplete<VehicleShortListModel>
                        options={vehicleOptions}
                        getOptionLabel={(v: VehicleShortListModel) =>
                            v.displayName ?? (v.number != null ? `#${v.number}` : `Vehicle ${v.id}`)
                        }
                        filterOptions={filterOptions}
                        value={selectedVehicle}
                        onChange={(_, v) => setSelectedVehicle(v == null || typeof v === "string" ? null : (v as VehicleShortListModel))}
                        loading={vehiclesLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vehicle (search by truck number or name)"
                                size="small"
                            />
                        )}
                    />
                    <Button
                        variant="contained"
                        onClick={handleRun}
                        disabled={!selectedVehicle || runLoading}
                    >
                        {runLoading ? "Running…" : "Run"}
                    </Button>
                    {runLoading && <LinearProgress />}
                    {error && (
                        <Typography color="error">{error}</Typography>
                    )}
                    {debugResult?.vehicleNotFound && (
                        <Typography color="error">Vehicle not found.</Typography>
                    )}
                    {orderResult && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Result:{" "}
                                <Chip
                                    size="small"
                                    label={orderResult.eligible ? "Eligible" : "Not eligible"}
                                    color={orderResult.eligible ? "success" : "default"}
                                />
                                {orderResult.notFound && (
                                    <Chip size="small" label="Order not found" color="warning" sx={{ ml: 1 }} />
                                )}
                            </Typography>
                            {vehicleSummary && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Vehicle ID: {vehicleSummary.vehicleId} | Pick-up radius: {vehicleSummary.pickUpRadius} mi
                                    {vehicleSummary.lat != null && vehicleSummary.lon != null && (
                                        <> | Location: {vehicleSummary.lat}, {vehicleSummary.lon}</>
                                    )}
                                </Typography>
                            )}
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                Cache: In posted order cache: {orderResult.inPostedOrderCache ? "Yes" : "No"} |
                                Vehicle mapped to order: {orderResult.vehicleMappedToOrderInCache ? "Yes" : "No"}
                            </Typography>
                            {orderResult.location && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Distance from current: {orderResult.location.distanceFromCurrent?.toFixed(2) ?? "—"} mi
                                    {orderResult.location.withinRadiusFromCurrent != null && (
                                        <> (within radius: {orderResult.location.withinRadiusFromCurrent ? "Yes" : "No"})</>
                                    )}
                                    {orderResult.location.distanceFromDestination != null && (
                                        <> | From destination: {orderResult.location.distanceFromDestination.toFixed(2)} mi</>
                                    )}
                                </Typography>
                            )}
                            {orderResult.conditions && orderResult.conditions.length > 0 && (
                                <Table size="small" sx={{ mt: 1 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Condition</TableCell>
                                            <TableCell>Applied</TableCell>
                                            <TableCell>Matched</TableCell>
                                            <TableCell>Vehicle value</TableCell>
                                            <TableCell>Order value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderResult.conditions.map((c, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{c.name}</TableCell>
                                                <TableCell>{c.applied ? "Yes" : "No"}</TableCell>
                                                <TableCell>{c.matched ? "Yes" : "No"}</TableCell>
                                                <TableCell>{c.vehicleValue ?? "—"}</TableCell>
                                                <TableCell>{c.orderValue ?? "—"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
